import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, EmptyState } from "@/components/Common";
import { UserCog, UserPlus, Trash2, ShieldAlert, Eye, AlertTriangle, Crown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABEL = { admin: "Administrator", pic: "PIC (View-Only)", pharmacy_manager: "Manager (View-Only)", viewer: "View-Only" };

// Roles the "Add User" form can create. Admin is intentionally the last option
// so it doesn't get picked by accident — the dialog also shows a warning banner
// when it's selected.
const ROLE_OPTIONS = [
  { value: "pic", label: "PIC (View-Only)" },
  { value: "pharmacy_manager", label: "Pharmacy Manager (View-Only)" },
  { value: "admin", label: "Administrator — full access" },
];

export default function UserManagement() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "pic" });

  const reload = useCallback(() => api.get("/users").then((r) => setUsers(r.data)).catch(() => {}), []);
  useEffect(() => { reload(); }, [reload]);

  const create = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("All fields are required");
    if (form.role === "admin" && !window.confirm(
      `Grant FULL ADMIN access to ${form.email}?\n\nThey'll be able to edit every record, manage users, view PHI, and delete other admins (except the last one).`
    )) return;
    try {
      await api.post("/users", form);
      toast.success(form.role === "admin" ? "Admin created" : "View-only user created");
      setOpen(false); setForm({ name: "", email: "", password: "", role: "pic" }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id, role) => {
    const msg = role === "admin"
      ? "Remove this administrator? Their access will be revoked immediately."
      : "Remove this user?";
    if (!window.confirm(msg)) return;
    try { await api.delete(`/users/${id}`); toast.success("User removed"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader testid="users-header" title="User Management" subtitle="Add administrators or create view-only accounts for PIC and Pharmacy Managers.">
        <Button data-testid="add-user-button" onClick={() => setOpen(true)} className="bg-pink-700 hover:bg-pink-800 gap-1.5"><UserPlus className="w-4 h-4" /> Add User</Button>
      </PageHeader>

      <Card className="p-0 overflow-hidden border-slate-200">
        {users.length === 0 ? <EmptyState icon={UserCog} title="No users yet" /> : (
          <Table data-testid="users-table">
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-slate-50/60">
                  <TableCell className="font-medium text-slate-800">{u.name}</TableCell>
                  <TableCell className="text-sm">{u.email}</TableCell>
                  <TableCell>
                    {u.is_master ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-900 border border-amber-300">
                        <Crown className="w-3 h-3" /> Master Admin
                      </span>
                    ) : (
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${u.role === "admin" ? "bg-pink-50 text-pink-800" : "bg-amber-50 text-amber-700"}`}>
                        {u.role === "admin" ? <ShieldAlert className="w-3 h-3" /> : <Eye className="w-3 h-3" />} {ROLE_LABEL[u.role] || u.role}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {u.is_master ? (
                      <span data-testid={`user-locked-${u.id}`} className="text-[11px] text-slate-400 italic">Protected · locked</span>
                    ) : u.id !== user?.id && (
                      <Button data-testid={`user-delete-${u.id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={() => remove(u.id, u.role)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-user-dialog">
          <DialogHeader><DialogTitle className="font-heading">New User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-slate-600">Name</Label><Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label className="text-xs text-slate-600">Email</Label><Input data-testid="user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label className="text-xs text-slate-600">Temporary Password</Label><Input data-testid="user-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div><Label className="text-xs text-slate-600">Role</Label>
              <select data-testid="user-role-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm">
                {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select></div>
            {form.role === "admin" && (
              <div data-testid="admin-warning" className="p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900 flex gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-700" />
                <div>
                  <b>Full access.</b> Administrators can edit every record, manage other users, view PHI,
                  reset PINs, and delete data. They can also remove other admins (except the last one).
                  Only invite people you fully trust with production data.
                </div>
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="user-save-button" onClick={create}
              className={form.role === "admin" ? "bg-rose-700 hover:bg-rose-800" : "bg-pink-700 hover:bg-pink-800"}>
              {form.role === "admin" ? "Grant Admin Access" : "Create User"}
            </Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
